import * as React from 'react';
import {cloneElement, ComponentPropsWithRef, ReactElement, ReactNode, useMemo, useRef, useState} from 'react';
import {UseFloatingOptions} from '@floating-ui/react/dist/floating-ui.react';
import {
    arrow,
    autoPlacement,
    FloatingFocusManager,
    offset,
    safePolygon,
    useDismiss,
    useFloating,
    useHover,
    useInteractions,
    useMergeRefs,
} from '@floating-ui/react';

import {FloatingArrowStyles} from '../FloatingArrowStyles';

interface TooltipProps {
    children: ReactNode;
    id: string;
    offset?: number;
    placement?: TooltipPlacement;
    shown?: boolean;
    title: string;
}

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right' | 'auto';

export const Tooltip = ({children, id, offset: theirOffset = 10, placement = `top`, shown = true, title}: TooltipProps) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState<boolean>(false);

    const arrowRef = useRef(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const middleware = [offset(theirOffset)];
    if (placement === 'auto') {
        middleware.push(autoPlacement());
    }
    const floatingConfigs: UseFloatingOptions = {
        open: uncontrolledOpen,
        onOpenChange: setUncontrolledOpen,
        middleware,
    };
    if (placement !== 'auto') {
        floatingConfigs.placement = placement;
    }
    middleware.push(
        arrow({
            element: arrowRef,
        }),
    );
    const {refs, floatingStyles, context, middlewareData} = useFloating(floatingConfigs);

    const {getFloatingProps, getReferenceProps} = useInteractions([
        useHover(context, {
            handleClose: safePolygon(),
        }),
        useDismiss(context),
    ]);

    const childrenRef = (children as ComponentPropsWithRef<'button'>).ref;
    const ref = useMergeRefs([context.refs.setReference, childrenRef]);
    const target = useMemo(() => {
        return cloneElement(
            children as ReactElement,
            getReferenceProps({
                ref,
                ...(children as ReactElement)?.props,
            }),
        );
    }, [children, ref, getReferenceProps]);
    /**
     * Template
     */
    if (!shown) {
        return <span>{children}</span>;
    }
    return (
        <FloatingArrowStyles
            ref={rootRef}
            className="tw-flex"
        >
            {target}
            {uncontrolledOpen && (
                <FloatingFocusManager
                    context={context}
                    modal
                >
                    <div>
                        <div
                            className={`tw-z-[1270] tw-m-0 tw-font-["Roboto"] tw-font-normal tw-leading-normal tw-text-start tw-no-underline tw-whitespace-normal tw-break-normal tw-text-sm tw-px-0 tw-opacity-90 focus:tw-outline-0`}
                            id={id}
                            style={floatingStyles}
                            ref={refs.setFloating}
                            {...getFloatingProps()}
                        >
                            <div
                                className={`arrow dark ${context.placement}`}
                                ref={arrowRef}
                                style={{
                                    position: 'absolute',
                                    left: middlewareData.arrow?.x,
                                    top: middlewareData.arrow?.y,
                                }}
                            ></div>
                            <div className="tw-max-w-[200px] tw-py-1 tw-px-2 tw-text-white tw-bg-gray-600 tw-rounded-sm tw-text-center">
                                {title}
                            </div>
                        </div>
                    </div>
                </FloatingFocusManager>
            )}
        </FloatingArrowStyles>
    );
};
